// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import SelectorModal from 'wdna-components/lib/modals/selectors/CustomSelectorModal';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning, messageBoxInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Edit from '../forms/customers/Edit';

import { SensorListDO } from '../../../sensors/requests';
import { client, ClientDO } from '../../requests';

type Props = unknown;

interface State {
  rowId?: number;
  update?: any;
  loading: boolean;
  data?: ClientDO[];
  valueListA?: SensorListDO[];
  valueListS?: string[];
  showGrid?: boolean;
  showSelector?: boolean;
  showValueSelector?: boolean;
}

export default class Customers extends React.Component <Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      add: true,
      del: true,
      assign: true,
      toggleItem: true,
      labels: {
        assign: 'Sensors',
      }
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      valueListA: [],
      valueListS: [],

      showGrid: false,
      showValueSelector: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const customerArray: ClientDO[] = await client<ClientDO[]>('list') || [];
      if (this.mounted) {
        this.setState({
          data: customerArray,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onAssign = (value: ClientDO): void => {
    const rowId: number = value.id;
    this.setState((prevState: State) => ({
      ...prevState,
      rowId,
    }));
    setTimeout(() => {
      this.loadValues(rowId).catch(() => { /* pass */ });
    }, 0);
  };

  public loadValues = async (rowId: number): Promise<void> => {
    try {
      const valueListFalse: SensorListDO[] = await client<SensorListDO[]>('sensors', String(rowId), { assigned: 'false' }) || [];
      const valueListTrue: SensorListDO[] = await client<SensorListDO[]>('sensors', String(rowId), { assigned: 'true' }) || [];

      const valueListS: string[] = [];
      valueListTrue.forEach((sensorAs: SensorListDO) => {
        valueListS.push(String(sensorAs.id));
      });

      this.setState((prevState: State) => ({
        ...prevState,
        update: window.getStr(),
        valueListA: valueListFalse.concat(valueListTrue),
        valueListS,
      }));
      this.updModal('showValueSelector', true);
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
  };

  public updModal = (modalName: string = 'none', sh: boolean = false): void => {
    this.setState((prevState: State) => ({
      ...prevState,
      update: window.getStr(),
      [modalName]: sh,
    }));
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'CuE';
    const title: string = rowId ? `Customer Edit: ${rowId}` : 'Customer New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'CUSTOMERS',
        title: 'Customers',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (value: ClientDO): void => {
    window.store.dispatch(removeTab('CuE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: ClientDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      client('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleChangeValues = (counters: any[]): void => {
    const { rowId } = this.state;

    if (rowId) {
      this.updModal('showValueSelector', false);

      const valueListS: any[] = [];
      counters.forEach((counter: any) => {
        valueListS.push(counter);
      });

      this.setState({
        valueListS,
      }, () => { this.handleSubmit(rowId); });
    }
  };

  public handleSubmit = (rowId: number): void => {
    const { valueListS } = this.state;

    messageBoxInfo('Edit', 'Do you want to accept this assignment?', () => {
      this.loadingStart();
      client('sensor', String(rowId), { sensorId: valueListS })
        ?.then(() => {
          messageInfo('Success', 'Sensors successfully assigned', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public render(): JSX.Element {
    const { loading, data, valueListA, valueListS, showValueSelector, showGrid } = this.state;

    const columnAlias: any = {
      selectedRow: ' ',
      id: 'ID',
      active: 'Active',
      validated: 'Validated',
      code: 'Code',
      name: 'Full name',
      email: 'Email',
      typeClientName: 'Type',
      companyName: 'Company',
    };
    const columnsToHide: any = [
      'password',
      'companyId',
      'typeClientId'
    ];
    const columnStyles: any = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      email: { textAlign: 'left' },
      typeClientName: { textAlign: 'left' },
      companyName: { textAlign: 'left' },
      active: { textAlign: 'center' },
      validated: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };
    const selectorAlias: any = {
      code: 'Code',
      name: 'Name',
      status: 'Status',
      companyName: 'Company',
      locationName: 'Location',
    };
    const selectorStyles: any = {
      code: { textAlign: 'right', minWidth: '50px' },
      name: { textAlign: 'left', minWidth: '135px' },
      status: { textAlign: 'left' },
      companyName: { textAlign: 'left', minWidth: '125px' },
      locationName: { textAlign: 'left' },
    };
    const selectorHide: any = [
      'code',
      'tsLast',
      'tsForwardLast',
      'tsCheck',
      'command1',
      'command2',
      'command3',
      'update',
      'persistent1',
      'persistent2',
      'companyId',
      'firmwareId',
      'firmwareVer',
      'firmwareFile',
      'typeSensorId',
      'typeSensorName',
      'locationId',
      'latitude',
      'longitude',
      'timeNc',
      'alarm',
      'adjusted',
      'dateInstall',
      'dateOnline',
      'dateOffline',
      'dateAction',
      'sim',
      'router',
      'comments',
      'companyType',
      'companyContact',
      'companyPhone',
      'companyEmail',
      'dateLast',
      'ipLast',
      'notifyNc',
      'update',
      'open',
      'shared',
      'alarm',
      'description',
      'alerts',
      'gatewayLast'
    ];

    return (
      <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          onAssign={this.onAssign}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
        />
        <SelectorModal
          alias={selectorAlias}
          items={valueListA || []}
          selectedItems={valueListS}
          title="Assign Sensors to customer"
          hiddenCols={selectorHide}
          customCellStyles={selectorStyles}
          show={showValueSelector}
          // eslint-disable-next-line react/jsx-no-bind
          onCancel={this.updModal.bind(this, 'showValueSelector', false)}
          onSave={this.handleChangeValues}
        />
      </div>
    );
  }
}
